import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import {
  createDownloadCategory,
  deleteDownload,
  deleteDownloadCategory,
  getDownloadSignedUrl,
  listDownloadCategories,
  listDownloads,
  renameDownloadCategory,
  type DownloadCategory,
  type DownloadListItem,
} from "@/lib/downloads.functions";
import { confirmDialog } from "@/lib/confirm-dialog";

export const Route = createFileRoute("/_admin/admin/downloads/")({
  head: () => ({
    meta: [
      { title: "Downloads – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DownloadsAdminPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let val = n;
  let idx = 0;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx++;
  }
  return `${val.toFixed(val >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function DownloadsAdminPage() {
  const listFn = useServerFn(listDownloads);
  const categoriesFn = useServerFn(listDownloadCategories);
  const deleteFn = useServerFn(deleteDownload);
  const signFn = useServerFn(getDownloadSignedUrl);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | "">("");
  const [manageCategories, setManageCategories] = useState(false);

  const { data: items, isLoading, error } = useQuery({
    queryKey: ["admin-downloads", { search, categoryId }],
    queryFn: () =>
      listFn({
        data: {
          search: search.trim() || undefined,
          categoryId: categoryId || null,
        },
      }),
  });
  const { data: categories } = useQuery({
    queryKey: ["admin-download-categories"],
    queryFn: () => categoriesFn(),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-downloads"] }),
  });

  // Gliederung nach Kategorie – analog zum Kundenbereich. Reihenfolge folgt
  // der Kategorienliste; Dateien ohne Kategorie bilden den letzten Abschnitt.
  const groups = useMemo(() => {
    const list = items ?? [];
    const buckets = new Map<string, { id: string; name: string; items: DownloadListItem[] }>();
    for (const c of categories ?? []) {
      buckets.set(c.id, { id: c.id, name: c.name, items: [] });
    }
    const uncategorized: DownloadListItem[] = [];
    for (const it of list) {
      const cat = it.category;
      if (!cat) {
        uncategorized.push(it);
        continue;
      }
      const bucket = buckets.get(cat.id);
      if (bucket) bucket.items.push(it);
      else buckets.set(cat.id, { id: cat.id, name: cat.name, items: [it] });
    }
    const result = Array.from(buckets.values()).filter((g) => g.items.length > 0);
    if (uncategorized.length > 0) {
      result.push({ id: "__none__", name: "Ohne Kategorie", items: uncategorized });
    }
    return result;
  }, [items, categories]);


  async function onDelete(item: DownloadListItem) {
    if (
      !(await confirmDialog({
        title: `Download „${item.title}“ löschen?`,
        description: "Die Datei wird aus dem Cockpit und dem Speicher entfernt.",
      }))
    )
      return;
    try {
      await removeMutation.mutateAsync(item.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  async function onPreview(item: DownloadListItem) {
    try {
      const res = await signFn({ data: { id: item.id } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Downloads</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Dateien für Kundinnen und Kunden – privat gespeichert, sichtbar für
            alle Agenturkunden mit Cockpit-Zugang.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setManageCategories(true)}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary"
          >
            <Settings2 className="h-4 w-4" /> Kategorien
          </button>
          <Link
            to="/admin/downloads/$id"
            params={{ id: "new" }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" /> Neuer Download
          </Link>
        </div>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Titel oder Dateiname"
            className="rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-2 text-sm"
        >
          <option value="">Alle Kategorien</option>
          {(categories ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {/* Ticket AV6: Kundenfilter entfernt — alle Dateien gelten für alle Agenturkunden. */}
        {(search || categoryId) && (
          <button
            onClick={() => {
              setSearch("");
              setCategoryId("");
            }}
            className="text-xs text-muted-foreground underline"
          >
            Filter zurücksetzen
          </button>
        )}

      </div>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-border bg-background p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Laden …
        </div>
      ) : groups.length === 0 ? (
        <p className="mt-6 rounded-lg border border-border bg-background p-6 text-sm text-muted-foreground">
          {search || categoryId
            ? "Keine Downloads für die gewählten Filter."
            : "Noch keine Downloads angelegt."}
        </p>
      ) : (
        <div className="mt-8 space-y-10">
          {groups.map((g) => (
            <section key={g.id}>
              <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-2">
                <h2 className="font-display text-lg font-extrabold text-primary">
                  {g.name}
                </h2>
                <span className="text-xs text-muted-foreground">
                  {g.items.length} {g.items.length === 1 ? "Datei" : "Dateien"}
                </span>
              </div>
              <div className="overflow-hidden rounded-lg border border-border bg-background">
                <ul className="divide-y divide-border">
                  {g.items.map((it) => (
                    <li key={it.id} className="flex flex-wrap items-start gap-4 p-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="mt-0.5 rounded bg-secondary p-2 text-muted-foreground">
                          <Download className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-display text-base font-extrabold text-primary">
                            {it.title}
                          </p>
                          {it.description && (
                            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                              {it.description}
                            </p>
                          )}
                          {/*
                            Ticket AV6: Spalten „Sichtbarkeit" und „Dateityp" entfernt —
                            alle Dateien gelten für alle Agenturkunden, der Dateityp hatte
                            nur eine Ausprägung. Stattdessen Dateiname und Größe deutlicher.
                          */}
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="truncate font-mono text-foreground">{it.file_name}</span>
                            <span>·</span>
                            <span className="tabular-nums">{formatBytes(it.size_bytes)}</span>
                            <span>·</span>
                            <span>{dateFmt.format(new Date(it.created_at))}</span>
                          </div>

                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          onClick={() => onPreview(it)}
                          className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Vorschau
                        </button>
                        <Link
                          to="/admin/downloads/$id"
                          params={{ id: it.id }}
                          className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                        </Link>
                        <button
                          onClick={() => onDelete(it)}
                          aria-label="Löschen"
                          className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      )}


      {manageCategories && (
        <CategoriesDialog onClose={() => setManageCategories(false)} />
      )}
    </div>
  );
}

function CategoriesDialog({ onClose }: { onClose: () => void }) {
  const listFn = useServerFn(listDownloadCategories);
  const createFn = useServerFn(createDownloadCategory);
  const renameFn = useServerFn(renameDownloadCategory);
  const deleteFn = useServerFn(deleteDownloadCategory);
  const qc = useQueryClient();
  const { data: categories, isLoading } = useQuery({
    queryKey: ["admin-download-categories"],
    queryFn: () => listFn(),
  });
  const [newName, setNewName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-download-categories"] });
    qc.invalidateQueries({ queryKey: ["admin-downloads"] });
  };

  async function onCreate() {
    setErr(null);
    if (!newName.trim()) return;
    try {
      await createFn({ data: { name: newName.trim() } });
      setNewName("");
      invalidate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function onRename(cat: DownloadCategory) {
    const name = prompt("Neuer Name", cat.name)?.trim();
    if (!name || name === cat.name) return;
    try {
      await renameFn({ data: { id: cat.id, name } });
      invalidate();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  async function onDelete(cat: DownloadCategory) {
    if (
      !(await confirmDialog({
        title: `Kategorie „${cat.name}“ löschen?`,
        description: "Zugeordnete Downloads erscheinen danach unter „Ohne Kategorie“.",
      }))
    )
      return;
    try {
      await deleteFn({ data: { id: cat.id } });
      invalidate();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">Kategorien verwalten</h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCreate();
            }}
            placeholder="Neue Kategorie"
            className="flex-1 rounded-md border border-input bg-background p-2 text-sm"
          />
          <button
            onClick={onCreate}
            disabled={!newName.trim()}
            className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            Anlegen
          </button>
        </div>
        {err && (
          <p className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
            {err}
          </p>
        )}
        <div className="mt-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Laden …</p>
          ) : !categories || categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Kategorien.</p>
          ) : (
            <ul className="divide-y divide-border rounded border border-border">
              {categories.map((c) => (
                <li key={c.id} className="flex items-center justify-between p-3">
                  <span className="text-sm font-semibold">{c.name}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onRename(c)}
                      className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                    >
                      Umbenennen
                    </button>
                    <button
                      onClick={() => onDelete(c)}
                      className="text-xs font-semibold text-destructive hover:underline"
                    >
                      Löschen
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
